package app.footyguide.web.data

import app.footyguide.core.theme.ClubCatalog

/**
 * Repositório de times — Firestore SDK + localStorage cache.
 */
class WebTeamRepository {
    data class TeamData(
        val id: String,
        val name: String,
        val logo: String? = null,
        val shortName: String = "",
        val country: String = "BR",
        val slug: String? = null,
        val competitions: List<String> = emptyList(),
        val nameByLocale: Map<String, String> = emptyMap(),
        val primaryColor: String? = null,
        val secondaryColor: String? = null,
        val tertiaryColor: String? = null,
        val isDark: Boolean = true
    )

    private var memCache: Map<String, TeamData>? = null

    internal fun fallbackTeams(): Map<String, TeamData> {
        val map = mutableMapOf<String, TeamData>()
        ClubCatalog.CLUBS.forEach { club ->
            val teamData = TeamData(
                id = club.slug,
                name = club.name,
                logo = club.logoUrl,
                shortName = club.initials,
                country = "BR",
                slug = club.slug,
                competitions = listOf("brasileirao"),
                primaryColor = club.c1,
                secondaryColor = club.c2,
                tertiaryColor = club.c3,
                isDark = true,
            )
            map[club.slug] = teamData
        }
        return map
    }

    suspend fun getAll(): Map<String, TeamData> {
        memCache?.let { return it }

        return try {
            val baseManifest = fallbackTeams().toMutableMap()
            val docs = FirestoreSdk.collection("teams")
            if (docs.isEmpty()) {
                memCache = baseManifest
                return baseManifest
            }
            docs.forEach { doc ->
                val id = doc.id
                val catalogClub = ClubCatalog.bySlug(id)
                val name = catalogClub?.name ?: doc.string("name") ?: id
                val theme = doc.nestedMap("theme")
                val competitions = doc.stringList("competitions").takeIf { it.isNotEmpty() }
                    ?: listOfNotNull(doc.string("league")).ifEmpty {
                        if (catalogClub != null) listOf("brasileirao") else emptyList()
                    }
                val nameByLocale = doc.stringMap("nameByLocale")
                    .filterValues { it.isNotBlank() }
                val rawPrimary = theme?.string("primaryColor") ?: doc.string("primaryColor")
                val safePrimary = catalogClub?.c1 ?: rawPrimary?.takeIf { it.uppercase() != "#FFFFFF" } ?: "#0B6E4F"
                val safeSecondary = catalogClub?.c2 ?: theme?.string("secondaryColor") ?: doc.string("secondaryColor")
                val safeTertiary = catalogClub?.c3 ?: theme?.string("tertiaryColor") ?: doc.string("tertiaryColor")

                baseManifest[id] = TeamData(
                    id = id,
                    name = name,
                    logo = doc.string("logo")?.takeIf { it.isNotBlank() } ?: catalogClub?.logoUrl,
                    shortName = catalogClub?.initials ?: doc.string("shortName") ?: name.take(3).uppercase(),
                    country = doc.string("country") ?: "BR",
                    slug = catalogClub?.slug ?: doc.string("slug"),
                    competitions = competitions,
                    nameByLocale = nameByLocale,
                    primaryColor = safePrimary,
                    secondaryColor = safeSecondary,
                    tertiaryColor = safeTertiary,
                    isDark = theme?.boolean("isDark") ?: doc.boolean("isDark") ?: true
                )
            }
            memCache = baseManifest
            LocalCache.set(CACHE_KEY, serializeToJson(baseManifest))
            baseManifest
        } catch (_: Exception) {
            val cached = LocalCache.get(CACHE_KEY)
            if (cached != null) {
                val result = parseFromJson(cached)
                if (result.isNotEmpty()) {
                    memCache = result
                    return result
                }
            }
            val fallback = fallbackTeams()
            memCache = fallback
            fallback
        }
    }

    private fun serializeToJson(teams: Map<String, TeamData>): String {
        val items = teams.entries.joinToString(",") { (id, t) ->
            val logoStr = t.logo?.let { """"${it.replace("\"", "\\\"")}"""" } ?: "null"
            val pColorStr = t.primaryColor?.let { """"$it"""" } ?: "null"
            val sColorStr = t.secondaryColor?.let { """"$it"""" } ?: "null"
            val tColorStr = t.tertiaryColor?.let { """"$it"""" } ?: "null"
            val slugStr = t.slug?.let { """"${it.replace("\"", "\\\"")}"""" } ?: "null"
            val competitionsStr = t.competitions.joinToString(prefix = "[", postfix = "]") { """"${it.replace("\"", "\\\"")}"""" }
            val nameByLocaleStr = t.nameByLocale.entries.joinToString(prefix = "{", postfix = "}") { (locale, value) ->
                """"${locale.replace("\"", "\\\"")}":"${value.replace("\"", "\\\"")}""""
            }
            """{"id":"$id","name":"${t.name}","shortName":"${t.shortName}","country":"${t.country}","slug":$slugStr,"competitions":$competitionsStr,"nameByLocale":$nameByLocaleStr,"logo":$logoStr,"primaryColor":$pColorStr,"secondaryColor":$sColorStr,"tertiaryColor":$tColorStr,"isDark":${t.isDark}}"""
        }
        return "[$items]"
    }

    private fun parseFromJson(json: String): Map<String, TeamData> {
        return try {
            val arr = JSON.parse<dynamic>(json)
            val length = (arr.length as? Number)?.toInt() ?: 0
            val resultMap = mutableMapOf<String, TeamData>()
            for (i in 0 until length) {
                val item = arr[i]
                val id = item.id as? String ?: continue
                val name = item.name as? String ?: id

                val rawComps = item.competitions
                val competitions = mutableListOf<String>()
                if (rawComps != undefined && rawComps != null) {
                    val compsArr = rawComps
                    val compsLen = (compsArr.length as? Number)?.toInt() ?: 0
                    for (j in 0 until compsLen) {
                        val c = compsArr[j] as? String
                        if (c != null) competitions.add(c)
                    }
                }
                if (competitions.isEmpty()) {
                    (item.league as? String)?.let { competitions.add(it) }
                }

                val rawLocales = item.nameByLocale
                val nameByLocale = mutableMapOf<String, String>()
                if (rawLocales != undefined && rawLocales != null) {
                    val locales = rawLocales
                    val localeKeys = js("Object.keys(locales)") as Array<String>
                    localeKeys.forEach { locale ->
                        val value = locales[locale] as? String
                        if (!value.isNullOrBlank()) {
                            nameByLocale[locale] = value
                        }
                    }
                }

                resultMap[id] = TeamData(
                    id = id,
                    name = name,
                    logo = item.logo as? String,
                    shortName = item.shortName as? String ?: name.take(3).uppercase(),
                    country = item.country as? String ?: "BR",
                    slug = item.slug as? String,
                    competitions = competitions,
                    nameByLocale = nameByLocale,
                    primaryColor = item.primaryColor as? String,
                    secondaryColor = item.secondaryColor as? String,
                    tertiaryColor = item.tertiaryColor as? String,
                    isDark = if (item.isDark == undefined) true else item.isDark as Boolean
                )
            }
            resultMap
        } catch (_: Exception) { emptyMap() }
    }

    companion object {
        private const val CACHE_KEY = "fg-cache-teams-v23"

        /** IDs fixos dos 20 clubes da Série A 2026 em ordem alfabética */
        val SERIE_A_IDS = setOf(
            "athleticopr", "atleticomg", "bahia", "botafogo", "bragantino",
            "chapecoense", "corinthians", "coritiba", "cruzeiro", "flamengo",
            "fluminense", "gremio", "internacional", "mirassol", "palmeiras",
            "remo", "santos", "saopaulo", "vasco", "vitoria"
        )
    }
}
